import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useIsomorphicLayoutEffect } from '@shared/utils/hooks/useIsomorphicLayoutEffect';
import { useWindowResizeSubscriber } from '@shared/utils/hooks/useWindowResize';
import { withWindow } from '@shared/utils/withWindow';
import type { BoxProps } from '@withjoy/joykit';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRefinementsPanelProvider } from '../RefinementsPanel';

const CLOSE_QUICK_ADD_FORM_TIMEOUT = 0;
const OPEN_QUICK_ADD_FORM_TIMEOUT = 0;
const OPEN_QUICK_ADD_FORM_TRANSITION_TIMEOUT = 0;
const SET_TILE_HEIGHT_TIMEOUT = 600;

export const useProductTileDesktopView = ({ isMobile }: { isMobile: boolean }) => {
  const [{ isOpen }, setState] = useState<{ isOpen: boolean }>({
    isOpen: false,
  });

  const styledProductTileContainerRef = useRef<HTMLDivElement>(null);
  const baseProductDetailsRef = useRef<HTMLDivElement>(null);
  const quickAddFormRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const quickAddFormOpenTimeoutRef = useRef<number | undefined>();
  const quickAddFormCloseTimeoutRef = useRef<number | undefined>();

  const [height, setHeight] = useState<number>();

  const setProductTileHeight = useCallback(() => {
    if (styledProductTileContainerRef && styledProductTileContainerRef.current) {
      setHeight(styledProductTileContainerRef.current.clientHeight);
    }
  }, []);

  useWindowResizeSubscriber(setProductTileHeight);
  // subscribing to the useWindowResize hook is not enough because we need to set the height on load more products as well
  useIsomorphicLayoutEffect(setProductTileHeight, [setProductTileHeight]);

  // set the height on filters panel open/close
  const { isOpen: ifFilterOpen } = useRefinementsPanelProvider();
  useEffect(() => {
    setTimeout(() => {
      setProductTileHeight();
    }, SET_TILE_HEIGHT_TIMEOUT);
  }, [ifFilterOpen, setProductTileHeight]);

  const clearQuickAddFormCloseTimeout = useEventCallback(() => {
    withWindow(global => {
      global.clearTimeout(quickAddFormCloseTimeoutRef.current);
    });
  });

  const clearQuickAddFormOpenTimeout = useEventCallback(() => {
    withWindow(global => {
      global.clearTimeout(quickAddFormOpenTimeoutRef.current);
    });
  });

  const prepareToCloseQuickAddForm = useEventCallback((clearCurrentTimeout?: boolean) => {
    clearQuickAddFormOpenTimeout();
    withWindow(global => {
      quickAddFormCloseTimeoutRef.current = global.setTimeout(() => {
        setState(prev => ({ ...prev, isOpen: false }));
      }, CLOSE_QUICK_ADD_FORM_TIMEOUT);
    });
  });

  const prepareToOpenQuickAddForm = useEventCallback(() => {
    clearQuickAddFormCloseTimeout();
    clearQuickAddFormOpenTimeout();
    withWindow(global => {
      quickAddFormOpenTimeoutRef.current = global.setTimeout(
        () => {
          setState({ isOpen: true });
        },
        isOpen ? OPEN_QUICK_ADD_FORM_TRANSITION_TIMEOUT : OPEN_QUICK_ADD_FORM_TIMEOUT,
      );
    });
  });

  const getRootProps = useCallback(() => {
    return {
      zIndex: 4,
      onMouseLeave: () => {
        clearQuickAddFormOpenTimeout();
        setState(prev => ({ ...prev, isOpen: false }));
      },
    };
  }, [clearQuickAddFormOpenTimeout]);

  const getBaseProductDetailsProps = useCallback(() => {
    return {
      onMouseEnter: () => {
        prepareToOpenQuickAddForm();
      },
      onMouseLeave: () => {
        prepareToCloseQuickAddForm();
      },
    };
  }, [prepareToCloseQuickAddForm, prepareToOpenQuickAddForm]);

  const getQuickAddFormProps = useCallback(() => {
    const propsValue: BoxProps = {
      onMouseEnter: () => {
        clearQuickAddFormCloseTimeout();
      },
      onMouseLeave: () => {
        prepareToCloseQuickAddForm();
      },
    };

    return propsValue;
  }, [prepareToCloseQuickAddForm, clearQuickAddFormCloseTimeout]);

  if (!isMobile) {
    return {
      getQuickAddFormProps,
      getBaseProductDetailsProps,
      getRootProps,

      styledProductTileContainerRef,
      baseProductDetailsRef,
      quickAddFormRef,
      containerRef,
      isOpen,
      height,
    };
  } else {
    return {
      getQuickAddFormProps: () => ({}),
      getBaseProductDetailsProps: () => ({}),
      getRootProps: () => ({}),

      baseProductDetailsRef,
      quickAddFormRef,
      containerRef,
      isOpen: false,
    };
  }
};
